/**
 * modal.js — the shared modal primitive (styles in src/styles/ui-kit.css).
 *
 * openModal wires the whole lifecycle once: scrim + panel, Escape, scrim
 * click, the header close button, focus restore. Content is an HTML string
 * (callers escape their own data, same contract as every route renderer) or
 * an Element. Appended to document.body, which is why the m5t-modal classes
 * are unscoped in ui-kit.css.
 *
 *   const { el, close } = openModal({ label: 'Preview', content, size: 'wide' });
 */

let openCount = 0;

export function openModal({ label = '', content = '', size = '', onClose = null } = {}) {
  const previouslyFocused = document.activeElement;

  const scrim = document.createElement('div');
  scrim.className = 'm5t-modal-scrim';
  scrim.innerHTML = `
    <div class="m5t-modal${size === 'wide' ? ' m5t-modal--wide' : ''}" role="dialog" aria-modal="true" aria-label="${escAttr(label)}" tabindex="-1">
      <div class="m5t-modal__head">
        <h2 class="m5t-modal__title"></h2>
        <button type="button" class="m5t-modal__close" aria-label="Close">&times;</button>
      </div>
      <div class="m5t-modal__body"></div>
    </div>`;
  scrim.querySelector('.m5t-modal__title').textContent = label;
  const dialog = scrim.querySelector('.m5t-modal');

  const body = scrim.querySelector('.m5t-modal__body');
  if (content instanceof Element) body.appendChild(content);
  else body.innerHTML = String(content);

  let closed = false;
  const close = (result) => {
    if (closed) return;
    closed = true;
    openCount = Math.max(0, openCount - 1);
    document.removeEventListener('keydown', onKey, true);
    scrim.classList.remove('open');
    const finish = () => {
      scrim.remove();
      try { previouslyFocused?.focus?.(); } catch { /* focus restore is best-effort */ }
      if (onClose) onClose(result);
    };
    // Match the CSS exit transition; instant under the kill switches.
    if (document.documentElement.classList.contains('ff-no-animations')
      || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) finish();
    else setTimeout(finish, 180);
  };

  // Keyboard focus containment: aria-modal promises assistive tech the page
  // behind the scrim is inert, but the scrim only blocks the pointer, so Tab
  // has to be trapped here or it walks into the obscured page. Visibility is
  // attribute/inline-style based (not layout based) so it also holds in jsdom.
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const isVisible = (el) => {
    for (let n = el; n && n !== scrim; n = n.parentElement) {
      if (n.hidden || n.style?.display === 'none' || n.style?.visibility === 'hidden') return false;
    }
    return true;
  };

  const onKey = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(undefined); return; }
    if (e.key !== 'Tab' || closed) return;
    const focusables = [...dialog.querySelectorAll(FOCUSABLE)].filter(isVisible);
    if (!focusables.length) { e.preventDefault(); dialog.focus(); return; }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (!dialog.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  };

  scrim.addEventListener('click', (e) => { if (e.target === scrim) close(undefined); });
  scrim.querySelector('.m5t-modal__close').addEventListener('click', () => close(undefined));
  document.addEventListener('keydown', onKey, true);

  document.body.appendChild(scrim);
  openCount++;
  // Land focus on the dialog itself so keyboard users start inside the trap.
  // Callers that place their own initial focus (confirmDialog focuses Cancel
  // right after openModal returns) simply move focus on from here; the
  // containment check keeps us from stealing focus already placed inside.
  if (!dialog.contains(document.activeElement)) dialog.focus();
  // Next frame so the open transition can run.
  requestAnimationFrame(() => scrim.classList.add('open'));

  return { el: scrim, close };
}

function escAttr(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}
